import React, { useState, useEffect, useMemo, useRef } from 'react';
import WalkthroughButton from './WalkthroughButton';
import ScreenerTourController from './Screener/ScreenerTourController';
import {
  runScreenerBackend,
  getSavedParameters,
  saveParameterSet,
  deleteParameterSet
} from '../services/api';
import { isUsageLimitError, getUsageLimitData, isFeatureAccessError, getFeatureAccessData } from '../utils/usageLimitHelper';
import UsageLimitModal from './UsageLimitModal';
import FeatureAccessModal from './FeatureAccessModal';
import ScreenerModeToggle from './ScreenerModeToggle';
import ScreenerBasicForm from './ScreenerBasicForm';
import ScreenerIntermediateForm from './ScreenerIntermediateForm';
import MobileScreenerBasicForm from './Screener/MobileScreenerBasicForm';
import MobileScreenerIntermediateForm from './Screener/MobileScreenerIntermediateForm';
import ScreenerSavedParams from './ScreenerSavedParams';
import { isIntradayShapedParam } from '../utils/presetMappings';
import ScreenerResponsive from './Screener/ScreenerResponsive';
import ScreenerQueue from './ScreenerQueue';
import StandardLoader from './StandardLoader';
import { useMediaQuery } from '../hooks/useMediaQuery';

function ScreenerResults() {
  // Coerce stale modes in localStorage: 'advanced' → 'intermediate', and
  // 'intraday' → 'basic' now that short-dated screening lives in its own /0dte
  // module. A stale intraday selection also means the cached results are
  // short-dated rows, which the Basic/Intermediate views would render with the
  // wrong day-count semantics — so drop that cache once on the way through.
  const initialMode = (() => {
    try {
      const stored = localStorage.getItem('screenerMode');
      if (stored === 'advanced') return 'intermediate';
      if (stored === 'intraday') {
        localStorage.setItem('screenerMode', 'basic');
        localStorage.removeItem('screener_last_results');
        localStorage.removeItem('screener_last_results_timestamp');
        return 'basic';
      }
      if (stored === 'basic' || stored === 'intermediate') return stored;
    } catch (e) { /* ignore */ }
    return 'basic';
  })();
  const [mode, setMode] = useState(initialMode);
  const [paramSets, setParamSets] = useState([]);  // Start with empty queue
  // Counter bumped on every explicit "+ Add to Queue" — tells ScreenerQueue to
  // auto-expand. Direct runs never bump it, so the queue card stays collapsed.
  const [queueExpandSignal, setQueueExpandSignal] = useState(0);
  const [savedParams, setSavedParams] = useState([]);
  // Presets are stored in one shared table; short-dated ones belong to the
  // /0dte module and only clutter this list.
  const screenerParams = useMemo(
    () => savedParams.filter(p => !isIntradayShapedParam(p)),
    [savedParams]
  );
  const [results, setResults] = useState(null);
  const [lastRunParamSets, setLastRunParamSets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageLimitData, setUsageLimitData] = useState(null);
  const [featureAccessData, setFeatureAccessData] = useState(null);
  const [leftPanelHeight, setLeftPanelHeight] = useState(null);
  // Pulse-trigger that fires the auto-scroll to the results section after a
  // successful run (from either the form's "Run" CTA or the queue's "Run").
  // We use a counter (not a boolean) so back-to-back runs always re-fire even
  // if the previous scroll already happened.
  const [scrollResultsTick, setScrollResultsTick] = useState(0);
  const resultsAnchorRef = useRef(null);

  // Add form refs
  const basicFormRef = useRef(null);
  const intermediateFormRef = useRef(null);
  const leftPanelRef = useRef(null);

  // Mobile detection
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Restore cached results on mount
  useEffect(() => {
    fetchSavedParams();

    // Try to restore cached screener results from localStorage
    try {
      const cachedResults = localStorage.getItem('screener_last_results');
      const cachedTimestamp = localStorage.getItem('screener_last_results_timestamp');

      if (cachedResults && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp);
        const hoursSinceCache = (Date.now() - timestamp) / (1000 * 60 * 60);

        // Only restore if less than 24 hours old
        if (hoursSinceCache < 24) {
          const parsed = JSON.parse(cachedResults);
          setResults(parsed);
          console.log(`✅ Restored ${parsed.length || 0} screener results from cache (${Math.round(hoursSinceCache * 60)} minutes old)`);
        } else {
          // Clear stale cache
          localStorage.removeItem('screener_last_results');
          localStorage.removeItem('screener_last_results_timestamp');
          console.log('Cleared stale screener cache (>24hrs old)');
        }
      }
    } catch (err) {
      console.error('Error restoring screener cache:', err);
    }

    // eslint-disable-next-line
  }, []);

  // Measure left panel height for saved params scrollable area
  useEffect(() => {
    const measureLeftPanel = () => {
      if (leftPanelRef.current && !isMobile) {
        const height = leftPanelRef.current.offsetHeight;
        setLeftPanelHeight(height);
      }
    };

    // Initial measurement after render
    measureLeftPanel();

    // Re-measure on resize
    window.addEventListener('resize', measureLeftPanel);

    // Use ResizeObserver for more accurate tracking of content changes
    const resizeObserver = new ResizeObserver(measureLeftPanel);
    if (leftPanelRef.current) {
      resizeObserver.observe(leftPanelRef.current);
    }

    return () => {
      window.removeEventListener('resize', measureLeftPanel);
      resizeObserver.disconnect();
    };
  }, [mode, isMobile]);

  // After a fresh screener run, jump to the top of the results section.
  // Skipped on mount-from-cache (scrollResultsTick stays 0 until a run fires).
  // requestAnimationFrame lets the new <ScreenerResponsive> commit before we
  // measure its position.
  useEffect(() => {
    if (scrollResultsTick === 0) return;
    if (!resultsAnchorRef.current) return;
    const raf = requestAnimationFrame(() => {
      try {
        resultsAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        // Older browsers without smooth-scroll support
        resultsAnchorRef.current.scrollIntoView();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [scrollResultsTick]);

  const fetchSavedParams = async () => {
    try {
      const res = await getSavedParameters();
      setSavedParams(res.data);
    } catch (err) {
      console.error('Error fetching saved parameters:', err);
    }
  };

  // Extracted from handleSubmit so it can be reused by both the queue's "Run"
  // button (handleSubmit) and the form's new "Run X Scenarios" button
  // (handleAddAndRun, which adds scenarios then immediately runs).
  const runScreenerWithParams = async (paramsToRun) => {
    if (!Array.isArray(paramsToRun) || paramsToRun.length === 0) return;
    console.log(`🎯 runScreenerWithParams called with ${paramsToRun.length} scenario(s).`);

    setLoading(true);
    setError(null);

    try {
      const formatted = paramsToRun.map((p, index) => {
        console.log(`🔄 Formatting scenario ${index + 1}/${paramsToRun.length}:`, p.label);
        return {
          ...p,
          tickers: Array.isArray(p.tickers)
            ? p.tickers
            : p.tickers.split(',').map((t) => t.trim().toUpperCase()),
          strike_pct: parseFloat(p.strike_pct),
          stock_gain_pct: parseFloat(p.stock_gain_pct),
          allocation: parseFloat(p.allocation),
          days_to_gain: parseInt(p.days_to_gain),
          days_until_exp: parseInt(p.days_until_exp),
        };
      });

      console.log(`📤 Sending ${formatted.length} formatted scenario(s) to backend:`, formatted);
      const res = await runScreenerBackend({ param_sets: formatted });
      setResults(res.data);
      setLastRunParamSets(formatted);
      // Pulse the scroll-trigger so the page auto-jumps to the results section
      // once the new view mounts. Counter increment guarantees a state change
      // even if the previous trigger value matches.
      setScrollResultsTick((t) => t + 1);

      // Cache results in localStorage for persistence
      try {
        localStorage.setItem('screener_last_results', JSON.stringify(res.data));
        localStorage.setItem('screener_last_results_timestamp', Date.now().toString());
        console.log('Cached screener results to localStorage');
      } catch (err) {
        console.error('Error caching screener results:', err);
      }
    } catch (err) {
      console.error(err);
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else if (isFeatureAccessError(err)) {
        setFeatureAccessData(getFeatureAccessData(err));
      } else {
        setError('Failed to run screener.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    await runScreenerWithParams(paramSets);
  };

  // Form's "Run X Scenarios" CTA: append the form's scenarios to the queue
  // AND run immediately so the queue reflects what was executed.
  const handleAddAndRun = async (scenariosToAdd) => {
    if (!Array.isArray(scenariosToAdd) || scenariosToAdd.length === 0) return;
    const next = [...paramSets, ...scenariosToAdd];
    setParamSets(next);
    await runScreenerWithParams(next);
  };

  const loadSavedScenario = (param) => {
    setParamSets([
      ...paramSets,
      {
        ...param,
        tickers: Array.isArray(param.tickers)
          ? param.tickers.join(', ')
          : param.tickers,
      },
    ]);
  };

  const deleteSavedScenario = async (id) => {
    await deleteParameterSet(id);
    fetchSavedParams();
  };

  const saveScenario = async (param) => {
    // Stamp the owning module so the shared saved-parameter store splits by an
    // explicit marker rather than by guessing from days_until_exp. Without it a
    // Basic/Intermediate preset that happens to use one of the 0DTE band
    // midpoints (4/14/45/75) would be misfiled into the 0DTE list.
    const payload = {
      ...param,
      extra: { ...(param.extra || {}), module: 'screener' },
      tickers: Array.isArray(param.tickers)
        ? param.tickers.map((t) => t.trim().toUpperCase())
        : param.tickers.split(',').map((t) => t.trim().toUpperCase()),
    };
    try {
      await saveParameterSet(payload);
      fetchSavedParams();
      return true;
    } catch (err) {
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else if (isFeatureAccessError(err)) {
        setFeatureAccessData(getFeatureAccessData(err));
      } else {
        console.error('Error saving parameter:', err);
        setError('Failed to save parameter.');
      }
      return false;
    }
  };

  const handleModeChange = (newMode) => {
    // Coerce retired modes: 'advanced' → intermediate, 'intraday' → the /0dte module
    const safeMode = newMode === 'advanced' ? 'intermediate'
      : newMode === 'intraday' ? 'basic'
      : newMode;
    setMode(safeMode);
    try { localStorage.setItem('screenerMode', safeMode); } catch (e) { /* ignore */ }
    setResults(null); // Clear results when switching modes
    setError(null);
  };

  const handleScenariosChange = (scenarios) => {
    setParamSets(scenarios);
  };

  // Queue management functions for Basic mode
  const addToQueue = (scenario) => {
    setParamSets(prev => [...prev, scenario]);
    setQueueExpandSignal(s => s + 1);
    console.log('✅ Added scenario to queue:', scenario.label);
  };

  const removeFromQueue = (index) => {
    setParamSets(prev => {
      const removed = prev[index];
      console.log('Removed from queue:', removed?.label || `Scenario ${index + 1}`);
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearQueue = () => {
    setParamSets([]);
    console.log('Cleared all scenarios from queue');
  };

  const canRunScreener = () => {
    return paramSets.length > 0;
  };

  // Add load from queue functionality
  const loadFromQueue = (index) => {
    const scenario = paramSets[index];
    if (!scenario) return;

    console.log('Loading scenario from queue:', scenario);

    if (mode === 'basic' && basicFormRef.current) {
      basicFormRef.current.loadScenario(scenario);
      removeFromQueue(index);
    } else if (mode === 'intermediate' && intermediateFormRef.current) {
      intermediateFormRef.current.loadScenario(scenario);
      removeFromQueue(index);
    }
  };

  // Add load from saved params functionality
  const loadFromSaved = (param) => {
    console.log('Loading saved parameter:', param);

    if (mode === 'basic' && basicFormRef.current) {
      basicFormRef.current.loadScenario(param);
    } else if (mode === 'intermediate' && intermediateFormRef.current) {
      intermediateFormRef.current.loadScenario(param);
    }
  };


  return (
    <div className="px-0 py-4 md:p-4 text-text" style={{ overflowX: 'hidden' }}>
      <div className="flex items-center gap-3 mb-6 px-4 md:px-0">
        <h2 className="text-2xl font-bold text-brand-white">Options Screener</h2>
        <WalkthroughButton sectionKey="screener" />
        <ScreenerTourController
          mode={mode}
          hasResults={Array.isArray(results) && results.length > 0}
          isMobile={isMobile}
          onModeChange={handleModeChange}
          formRef={mode === 'basic' ? basicFormRef : intermediateFormRef}
        />
      </div>

      <div className="px-4 md:px-0" data-tour="mode-toggle">
        <ScreenerModeToggle
          currentMode={mode}
          onModeChange={handleModeChange}
          isMobile={isMobile}
        />
      </div>

      {/* Mobile Layout - Pill-based single-screen forms (Load Preset surfaced via sheet) */}
      {isMobile ? (
        <form onSubmit={handleSubmit} className="mb-6">
          {mode === 'basic' && (
            <MobileScreenerBasicForm
              ref={basicFormRef}
              onAddToQueue={addToQueue}
              onAddAndRun={handleAddAndRun}
              onSaveScenario={saveScenario}
              queueCount={paramSets.length}
              loading={loading}
              savedParams={screenerParams}
              onLoadParam={loadFromSaved}
              onDeleteParam={deleteSavedScenario}
              onAddParamToQueue={addToQueue}
            />
          )}
          {mode === 'intermediate' && (
            <MobileScreenerIntermediateForm
              ref={intermediateFormRef}
              onAddToQueue={addToQueue}
              onAddAndRun={handleAddAndRun}
              onSaveScenario={saveScenario}
              queueCount={paramSets.length}
              loading={loading}
              savedParams={screenerParams}
              onLoadParam={loadFromSaved}
              onDeleteParam={deleteSavedScenario}
              onAddParamToQueue={addToQueue}
            />
          )}
        </form>
      ) : (
        /* Desktop split-panel layout wrapper */
        <div className="screener-layout-wrapper">
          {/* Left Panel - Form */}
          <div className="screener-left-panel" ref={leftPanelRef}>
            <form onSubmit={handleSubmit} className="space-y-6 mb-6">
              {mode === 'basic' && (
                <ScreenerBasicForm
                  ref={basicFormRef}
                  onAddToQueue={addToQueue}
                  onAddAndRun={handleAddAndRun}
                  onSaveScenario={saveScenario}
                  queueCount={paramSets.length}
                  loading={loading}
                />
              )}

              {mode === 'intermediate' && (
                <ScreenerIntermediateForm
                  ref={intermediateFormRef}
                  onAddToQueue={addToQueue}
                  onAddAndRun={handleAddAndRun}
                  onSaveScenario={saveScenario}
                  queueCount={paramSets.length}
                  loading={loading}
                />
              )}

            </form>
          </div>

          {/* Right Panel - Saved Parameters (Desktop only — mobile uses the form's slide-up sheet) */}
          <div className="screener-right-panel">
            <ScreenerSavedParams
              savedParams={screenerParams}
              onLoad={loadFromSaved}
              onDelete={deleteSavedScenario}
              onAddToQueue={addToQueue}
              maxContentHeight={leftPanelHeight}
            />
          </div>
        </div>
      )}

      {/* Full-width Queue Section (positioned after form area) */}
      <div className="queue-section-fullwidth px-4 md:px-0">
        <ScreenerQueue
          scenarios={paramSets}
          expandSignal={queueExpandSignal}
          onRemove={removeFromQueue}
          onClear={clearQueue}
          onLoad={loadFromQueue}
          onSave={saveScenario}
          onSubmit={handleSubmit}
          canRun={canRunScreener()}
          loading={loading}
        />
      </div>

      {/* ✅ Show loader */}
      <StandardLoader show={loading} />

      {error && <p className="text-error">{error}</p>}

      {/* Show "no results" message when results is an empty array */}
      {results && Array.isArray(results) && results.length === 0 && (
        <div className="card bg-yellow-900 bg-opacity-20 border-yellow-600 mt-6">
          <div className="flex items-start gap-4">
            <div className="text-4xl">⚠️</div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-yellow-400 mb-3">No Contracts Found</h3>
              <p className="text-gray-300 mb-4">
                The screener didn't find any option contracts matching your parameters. This can happen for several reasons:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-400 mb-4">
                <li>No options are available for the selected tickers in the specified expiration range</li>
                <li>The strike price filters are too restrictive</li>
                <li>The tickers may be invalid, delisted, or not have active options</li>
                <li>The API data provider doesn't have options data for these tickers</li>
              </ul>
              <div className="bg-blue-900 bg-opacity-30 border border-blue-600 rounded p-3">
                <p className="text-blue-300 font-semibold mb-2">Try These Adjustments:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-200 text-sm">
                  <li>Choose different strike positions (ITM, ATM, OTM)</li>
                  <li>Extend the expiration timeframe (try 3mo, 6mo, or 12mo+)</li>
                  <li>Use different tickers (try popular ones like SPY, AAPL, MSFT)</li>
                  <li>Check the backend console logs for detailed debugging information</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {results && Array.isArray(results) && results.length > 0 && (
        <div ref={resultsAnchorRef} data-tour="results-section" style={{ scrollMarginTop: '12px' }}>
          <ScreenerResponsive results={results} scenarioParams={lastRunParamSets} screenerMode={mode} />
        </div>
      )}

      {usageLimitData && (
        <UsageLimitModal data={usageLimitData} onClose={() => setUsageLimitData(null)} />
      )}

      {featureAccessData && (
        <FeatureAccessModal data={featureAccessData} onClose={() => setFeatureAccessData(null)} />
      )}

    </div>
  );
}

export default ScreenerResults;
